'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAuth, useGetProposalsInfinite, useMarketConfigCatalog } from '@morfi/backend-sdk-v2/client';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { marketTypeFilterParams, parseMarketTypeParam, useMarketTypeFilter } from '@/components/market/identity/market-identity-filter';
import type { FilterDimension, FilterValue } from '@/components/shared/sort-filter-bar';
import type { SortPillOption } from '@/components/shared/sort-pill-row';
import type { ChipOption } from '@/components/ui/chip-multi-select';
import { FireIconSolid, Plant04IconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import type { MultiSelectOption } from '@/components/ui/searchable-multi-select';
import { categoryNameToId, DISCOVERY_CATEGORIES, locationNameToId, PROPOSAL_LOCATIONS } from '@/config/proposal-options';
import { onEvent } from '@/events';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { useFormattedCurrency } from '@/lib/currency';
import { creatorParam, csvParam, dateRangeParams, dateRangeToParam, dollarsToD6, intString, type OutcomesBounds, paramToCsv, paramToDateRange, paramToRange, type Range, rangeParams, rangeToParam } from '@/lib/filters/feed-filters';
import { LAUNCHPAD_FEED_FIELDS } from '@/lib/sdk/projections/launchpad-feed';
import { LISTING_INDEX_PAGE_STACK_CLASS } from '@/lib/ui/layout';
import { ProposalFeedList } from './proposal-feed-list';
import { ProposalFeedSkeleton } from './proposal-feed-skeleton';
import { ProposalFeedToolbar } from './proposal-feed-toolbar';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProposalFeedProps {
  initialFilters?: {
    category?: string;
    status?: string;
    location?: string;
    search?: string;
  };
  showCreateButton?: boolean;
  /** Filter sheet open state — lifted to LaunchpadFeedLayout so the toolbar button (in LaunchpadHeader) can drive it. */
  filterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
  /** Watchlist toggle state — lifted to LaunchpadFeedLayout so the toolbar button (in LaunchpadHeader) can drive it. */
  watchlistActive?: boolean;
  onWatchlistActiveChange?: (active: boolean) => void;
}
type FeedTranslator = ReturnType<typeof useTranslations<'launchpad.feed'>>;
type FiltersTranslator = ReturnType<typeof useTranslations<'filters'>>;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Unsatisfiable-but-valid `pubkeys` filter for the empty-watchlist case. An
 * empty `pubkeys=` is dropped by the backend's filter guard, which then returns
 * EVERY proposal (MOR-806). The all-1s base58 string is the Solana system
 * pubkey (32 zero bytes); it decodes cleanly and matches no real proposal, so
 * the watchlist feed stays empty instead. Mirrors the markets feed (MOR-386).
 */
const WATCHLIST_EMPTY_SENTINEL = '11111111111111111111111111111111';
const CATEGORY_CHIP_OPTIONS: ChipOption[] = DISCOVERY_CATEGORIES.map(c => ({
  value: c.name,
  label: c.name,
  emoji: c.emoji
}));
const REGION_MULTI_OPTIONS: MultiSelectOption[] = PROPOSAL_LOCATIONS.map(l => ({
  value: l.name,
  label: l.name,
  emoji: l.emoji
}));

// Slider/date bounds — mirror the filter-overhaul mockup (`/tmp/morfi-filters`).
// The outcomes bounds (both ends) are sourced from PlatformConfig at runtime;
// these are only the pre-resolution fallbacks.
const FALLBACK_MIN_OUTCOMES = 2;
const FALLBACK_MAX_OUTCOMES = 12;
const P = {
  funding: {
    min: 0,
    max: 100
  },
  staked: {
    min: 0,
    max: 20_000
  },
  backers: {
    min: 0,
    max: 200
  },
  outcomes: {
    min: FALLBACK_MIN_OUTCOMES,
    max: FALLBACK_MAX_OUTCOMES
  }
} as const;
const PROPOSALS_CREATED_SPAN_DAYS = 180;

/** Read a FilterValue as a string[] (chipsMulti). */
function asStrArr(value: FilterValue | undefined): string[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')) {
    return value as string[];
  }
  return [];
}

/** Read a FilterValue as a MultiSelectOption[] (searchMulti / creatorMulti). */
function asOptArr(value: FilterValue | undefined): MultiSelectOption[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'object')) {
    return value as MultiSelectOption[];
  }
  return [];
}

/** Read a FilterValue as a `[lo, hi]` range, or `undefined` when not a range. */
function asRangeVal(value: FilterValue | undefined): Range | undefined {
  if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'number') {
    return [value[0], value[1] as number];
  }
  return undefined;
}
const PROPOSALS_FILTER_TYPE_BY_KEY: Record<string, 'category' | 'location' | 'funding' | 'staked' | 'backers' | 'outcomes' | 'createdAt' | 'creator'> = {
  category: 'category',
  location: 'location',
  funding: 'funding',
  staked: 'staked',
  backers: 'backers',
  outcomes: 'outcomes',
  createdAt: 'createdAt',
  creator: 'creator'
};
function summariseFilterValue(value: FilterValue | undefined): string {
  if (value === undefined || value === null) return 'all';
  if (typeof value === 'string') return value.length > 0 ? value : 'all';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'all';
    if (typeof value[0] === 'number') return `${value[0]}-${value[1] as number}`;
    if (typeof value[0] === 'string') return (value as string[]).join(',');
    return (value as MultiSelectOption[]).map(o => o.value).join(',');
  }
  return 'all';
}
function buildEmptyProposalsOverhaul(outcomesBounds: OutcomesBounds): Record<string, FilterValue | undefined> {
  return {
    location: [],
    funding: [P.funding.min, P.funding.max],
    staked: [P.staked.min, P.staked.max],
    backers: [P.backers.min, P.backers.max],
    outcomes: [outcomesBounds.min, outcomesBounds.max],
    createdAt: [0, 100],
    creator: []
  };
}

// Category is owned by the global top category nav (`CategoryNav`) via the URL
// `?category=`, NOT by the overhaul state — so it is preserved straight from the
// URL in the sync effect and intentionally absent from this serialiser.
function buildProposalsUrlParams(overhaul: Record<string, FilterValue | undefined>, outcomesBounds: OutcomesBounds, regionEnabled: boolean, identityEnabled: boolean): Record<string, string> {
  const out: Record<string, string> = marketTypeFilterParams(overhaul.marketType, identityEnabled);
  if (regionEnabled) {
    const regions = asOptArr(overhaul.location).map(o => o.value);
    if (regions.length > 0) out.location = regions.join(',');
  }
  const addRange = (key: string, range: Range | undefined, min: number, max: number): void => {
    if (range === undefined) return;
    const v = rangeToParam(range, min, max);
    if (v !== null) out[key] = v;
  };
  addRange('funding', asRangeVal(overhaul.funding), P.funding.min, P.funding.max);
  addRange('staked', asRangeVal(overhaul.staked), P.staked.min, P.staked.max);
  addRange('backers', asRangeVal(overhaul.backers), P.backers.min, P.backers.max);
  addRange('outcomes', asRangeVal(overhaul.outcomes), outcomesBounds.min, outcomesBounds.max);
  const created = asRangeVal(overhaul.createdAt);
  if (created !== undefined) {
    const v = dateRangeToParam(created);
    if (v !== null) out.createdAt = v;
  }
  return out;
}

// ---------------------------------------------------------------------------
// URL param defaults & validation sets
// ---------------------------------------------------------------------------

const DEFAULT_SORT = 'totalFunded';
const VALID_SORTS: ReadonlySet<string> = new Set(['totalFunded', 'createdAt', 'graduated']);
const VALID_STATUSES: ReadonlySet<string> = new Set(['active', 'needs_debate', 'pending_review', 'creating', 'rejected', 'expired']);
const VALID_CATEGORIES: ReadonlySet<string> = new Set(DISCOVERY_CATEGORIES.map(c => c.name));
const VALID_LOCATIONS: ReadonlySet<string> = new Set(PROPOSAL_LOCATIONS.map(l => l.name));

// ---------------------------------------------------------------------------
// Render-config builders (pure — kept at module scope so the component body
// stays readable; see the giant-component split for `proposal-feed`).
// ---------------------------------------------------------------------------

function buildProposalSortOptions(t: FeedTranslator): SortPillOption[] {
  return [{
    label: t('sortPopular'),
    value: 'totalFunded',
    prefix: FireIconSolid
  }, {
    label: t('sortNewest'),
    value: 'createdAt',
    prefix: Plant04IconSolid
  }, {
    label: t('sortGraduated'),
    value: 'graduated',
    prefix: Rocket01IconSolid
  }];
}
function buildProposalFilterDimensions(t: FeedTranslator, tFilters: FiltersTranslator, outcomesBounds: OutcomesBounds, regionEnabled: boolean, money: {
  rate: number;
  symbol: string;
}): FilterDimension[] {
  const anyLabel = tFilters('any');
  const dateLabels = {
    any: tFilters('any'),
    after: tFilters('after'),
    before: tFilters('before')
  };
  return [...(regionEnabled ? [{
    type: 'searchMulti' as const,
    key: 'location',
    label: t('regionLabel'),
    placeholder: t('searchRegions'),
    options: REGION_MULTI_OPTIONS
  }] : []), {
    type: 'creatorMulti',
    key: 'creator',
    label: t('creatorLabel'),
    placeholder: t('searchCreators'),
    emptyLabel: t('noCreators')
  }, {
    type: 'chipsMulti',
    key: 'category',
    label: t('categoryLabel'),
    options: CATEGORY_CHIP_OPTIONS
  }, {
    type: 'slider',
    key: 'funding',
    label: t('fundingProgressLabel'),
    min: P.funding.min,
    max: P.funding.max,
    format: {
      format: 'pct',
      anyLabel
    }
  }, {
    type: 'slider',
    key: 'staked',
    label: t('totalStakedLabel'),
    min: P.staked.min,
    max: P.staked.max,
    format: {
      format: 'money',
      anyLabel,
      rate: money.rate,
      symbol: money.symbol
    }
  }, {
    type: 'slider',
    key: 'backers',
    label: t('backersLabel'),
    min: P.backers.min,
    max: P.backers.max,
    format: {
      format: 'int',
      anyLabel
    }
  }, {
    type: 'slider',
    key: 'outcomes',
    label: t('outcomesLabel'),
    min: outcomesBounds.min,
    max: outcomesBounds.max,
    step: 1,
    format: {
      format: 'count',
      unit: t('outcomesUnit'),
      anyLabel
    }
  }, {
    type: 'dateSlider',
    key: 'createdAt',
    label: t('createdAtLabel'),
    spanDays: PROPOSALS_CREATED_SPAN_DAYS,
    labels: dateLabels
  }];
}

// Overhaul filter state → SDK args (unit conversion at the wire boundary).
// category/location → CSV of canonical slugs; money (staked) → D6; funding
// stays 0–100 percent; ints pass through; dates → ISO; creator → CSV of
// wallet addresses. `nowMs` anchors the relative createdAt range so the SDK
// query key stays stable across renders.
function buildProposalOverhaulArgs(params: {
  overhaul: Record<string, FilterValue | undefined>;
  outcomesBounds: OutcomesBounds;
  categorySelected: string[];
  creatorFilter: string | undefined;
  nowMs: number;
  regionEnabled: boolean;
  identityEnabled: boolean;
}): Record<string, string> {
  const {
    overhaul,
    outcomesBounds,
    categorySelected,
    creatorFilter,
    nowMs,
    regionEnabled,
    identityEnabled
  } = params;
  return {
    ...marketTypeFilterParams(overhaul.marketType, identityEnabled),
    ...csvParam('category', categorySelected.map(categoryNameToId)),
    ...(regionEnabled ? csvParam('location', asOptArr(overhaul.location).map(o => locationNameToId(o.value))) : {}),
    ...rangeParams(asRangeVal(overhaul.funding), P.funding.min, P.funding.max, 'fundingMin', 'fundingMax', intString),
    ...rangeParams(asRangeVal(overhaul.staked), P.staked.min, P.staked.max, 'stakedMin', 'stakedMax', dollarsToD6),
    ...rangeParams(asRangeVal(overhaul.backers), P.backers.min, P.backers.max, 'backersMin', 'backersMax', intString),
    ...rangeParams(asRangeVal(overhaul.outcomes), outcomesBounds.min, outcomesBounds.max, 'outcomesMin', 'outcomesMax', intString),
    ...dateRangeParams(asRangeVal(overhaul.createdAt), PROPOSALS_CREATED_SPAN_DAYS, nowMs, 'createdAfter', 'createdBefore'),
    // The "My Proposals" toggle still owns `creator`; only thread the
    // multi-creator filter when that toggle is off (mutually exclusive).
    ...(creatorFilter === undefined ? creatorParam('creator', asOptArr(overhaul.creator)) : {})
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProposalFeed(t0) {
  const $ = _c(105);
  const {
    initialFilters,
    showCreateButton: t1,
    filterOpen: filterOpenProp,
    onFilterOpenChange,
    watchlistActive: watchlistActiveProp,
    onWatchlistActiveChange
  } = t0;
  t1 === undefined ? false : t1;
  const t = useTranslations("launchpad.feed");
  const tFilters = useTranslations("filters");
  const regionEnabled = useFeatureGate("region") === true;
  const identityEnabled = useFeatureGate("creator_and_platform_markets") === true;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      platform: false
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const marketTypeFilter = useMarketTypeFilter(identityEnabled, t2);
  const {
    exchangeRate,
    currencySymbol
  } = useFormattedCurrency();
  const catalog = useMarketConfigCatalog();
  let t3;
  if ($[1] !== catalog.configs) {
    t3 = proposalOutcomeBounds(Object.values(catalog.configs ?? {}));
    $[1] = catalog.configs;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const outcomesBounds = t3;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  let t4;
  if ($[3] !== searchParams) {
    t4 = function readParam(key, validSet) {
      const raw = searchParams.get(key);
      if (raw !== null && validSet.has(raw)) {
        return raw;
      }
      return null;
    };
    $[3] = searchParams;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const readParam = t4;
  let t5;
  if ($[5] !== searchParams) {
    t5 = () => {
      const fromUrl = searchParams.get("sort");
      if (fromUrl !== null && VALID_SORTS.has(fromUrl)) {
        return fromUrl;
      }
      return DEFAULT_SORT;
    };
    $[5] = searchParams;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const [sortBy, setSortBy] = useState(t5);
  let t6;
  if ($[7] !== initialFilters?.status || $[8] !== readParam) {
    t6 = () => {
      const fromUrl_0 = readParam("status", VALID_STATUSES);
      if (fromUrl_0 !== null) {
        return fromUrl_0;
      }
      if (initialFilters?.status !== undefined && initialFilters?.status !== "") {
        return initialFilters?.status ?? null;
      }
      return null;
    };
    $[7] = initialFilters?.status;
    $[8] = readParam;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const [status, setStatus] = useState(t6);
  let t7;
  if ($[10] !== initialFilters?.search || $[11] !== searchParams) {
    t7 = () => searchParams.get("q") ?? initialFilters?.search ?? "";
    $[10] = initialFilters?.search;
    $[11] = searchParams;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const [search, setSearch] = useState(t7);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [isMyProposals, setIsMyProposals] = useState(false);
  const debounceTimerRef = useRef(null);
  let t8;
  if ($[13] !== outcomesBounds.max || $[14] !== outcomesBounds.min || $[15] !== searchParams) {
    t8 = () => ({
      marketType: parseMarketTypeParam(searchParams.get("marketType"), {
        platform: false
      }),
      location: REGION_MULTI_OPTIONS.filter(o => paramToCsv(searchParams.get("location"), VALID_LOCATIONS).includes(o.value)),
      funding: paramToRange(searchParams.get("funding"), P.funding.min, P.funding.max),
      staked: paramToRange(searchParams.get("staked"), P.staked.min, P.staked.max),
      backers: paramToRange(searchParams.get("backers"), P.backers.min, P.backers.max),
      outcomes: paramToRange(searchParams.get("outcomes"), outcomesBounds.min, outcomesBounds.max),
      createdAt: paramToDateRange(searchParams.get("createdAt")),
      creator: []
    });
    $[13] = outcomesBounds.max;
    $[14] = outcomesBounds.min;
    $[15] = searchParams;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const [overhaul, setOverhaul] = useState(t8);
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = function setOverhaulValue(key_0, value) {
      setOverhaul(prev => ({
        ...prev,
        [key_0]: value
      }));
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const setOverhaulValue = t9;
  const categoryParam = searchParams.get("category");
  const categorySelected = categoryParam !== null ? categoryParam.split(",").filter(_temp) : [];
  let t10;
  if ($[18] !== pathname || $[19] !== searchParams) {
    t10 = function applyCategoryToUrl(values) {
      const params = new URLSearchParams(searchParams.toString());
      if (values.length > 0) {
        params.set("category", values.join(","));
      } else {
        params.delete("category");
      }
      const qs = params.toString();
      window.history.replaceState(null, "", `${pathname}${qs.length > 0 ? `?${qs}` : ""}`);
    };
    $[18] = pathname;
    $[19] = searchParams;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const applyCategoryToUrl = t10;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      min: FALLBACK_MIN_OUTCOMES,
      max: FALLBACK_MAX_OUTCOMES
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const prevOutcomesBoundsRef = useRef(t11);
  let t12;
  let t13;
  if ($[22] !== outcomesBounds.max || $[23] !== outcomesBounds.min) {
    t12 = () => {
      const prev_0 = prevOutcomesBoundsRef.current;
      if (prev_0.min === outcomesBounds.min && prev_0.max === outcomesBounds.max) {
        return;
      }
      prevOutcomesBoundsRef.current = {
        min: outcomesBounds.min,
        max: outcomesBounds.max
      };
      setOverhaul(cur => {
        const range = asRangeVal(cur.outcomes);
        if (range !== undefined && range[0] === prev_0.min && range[1] === prev_0.max) {
          return {
            ...cur,
            outcomes: [outcomesBounds.min, outcomesBounds.max]
          };
        }
        return cur;
      });
    };
    t13 = [outcomesBounds.min, outcomesBounds.max];
    $[22] = outcomesBounds.max;
    $[23] = outcomesBounds.min;
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  useEffect(t12, t13);
  const {
    activeWallet
  } = useActiveWallet();
  const {
    isAuthenticated
  } = useAuth();
  const filterOpen = filterOpenProp ?? false;
  const setFilterOpen = onFilterOpenChange ?? _temp2;
  const watchlistActive = watchlistActiveProp ?? false;
  const setWatchlistActive = onWatchlistActiveChange ?? _temp3;
  const {
    watchlistLoading,
    watchlistPubkeys
  } = useProposalWatchlist(watchlistActive);
  let t14;
  let t15;
  if ($[26] !== search) {
    t14 = () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        setDebouncedSearch(search);
      }, SEARCH_DEBOUNCE_MS);
      return () => {
        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    };
    t15 = [search];
    $[26] = search;
    $[27] = t14;
    $[28] = t15;
  } else {
    t14 = $[27];
    t15 = $[28];
  }
  useEffect(t14, t15);
  let t16;
  if ($[29] !== identityEnabled || $[30] !== outcomesBounds || $[31] !== overhaul || $[32] !== regionEnabled) {
    t16 = new URLSearchParams(buildProposalsUrlParams(overhaul, outcomesBounds, regionEnabled, identityEnabled)).toString();
    $[29] = identityEnabled;
    $[30] = outcomesBounds;
    $[31] = overhaul;
    $[32] = regionEnabled;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const overhaulUrlKey = t16;
  let t17;
  let t18;
  if ($[34] !== debouncedSearch || $[35] !== overhaulUrlKey || $[36] !== pathname || $[37] !== searchParams || $[38] !== sortBy || $[39] !== status) {
    t17 = () => {
      const params_0 = new URLSearchParams();
      if (sortBy !== DEFAULT_SORT) {
        params_0.set("sort", sortBy);
      }
      const cat = searchParams.get("category");
      if (cat !== null) {
        params_0.set("category", cat);
      }
      for (const [k, v] of new URLSearchParams(overhaulUrlKey)) {
        params_0.set(k, v);
      }
      if (status !== null) {
        params_0.set("status", status);
      }
      if (debouncedSearch.length > 0) {
        params_0.set("q", debouncedSearch);
      }
      const qs_0 = params_0.toString();
      const target = qs_0.length > 0 ? `${pathname}?${qs_0}` : pathname;
      const current = `${pathname}${window.location.search}`;
      if (target !== current) {
        window.history.replaceState(null, "", target);
      }
    };
    t18 = [sortBy, status, debouncedSearch, pathname, overhaulUrlKey, searchParams];
    $[34] = debouncedSearch;
    $[35] = overhaulUrlKey;
    $[36] = pathname;
    $[37] = searchParams;
    $[38] = sortBy;
    $[39] = status;
    $[40] = t17;
    $[41] = t18;
  } else {
    t17 = $[40];
    t18 = $[41];
  }
  useEffect(t17, t18);
  const isGraduatedTab = sortBy === "graduated";
  const creatorFilter = isMyProposals && activeWallet !== null && activeWallet !== undefined ? activeWallet.address : undefined;
  const [nowMs] = useState(_temp4);
  const overhaulArgs = buildProposalOverhaulArgs({
    overhaul,
    outcomesBounds,
    categorySelected,
    creatorFilter,
    nowMs,
    regionEnabled,
    identityEnabled
  });
  const t19 = isGraduatedTab ? "updatedAt" : sortBy;
  const t20 = isGraduatedTab ? "live" : status ?? "all";
  let t21;
  if ($[42] !== isGraduatedTab) {
    t21 = isGraduatedTab ? {} : {
      excludeStatus: "live"
    };
    $[42] = isGraduatedTab;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== debouncedSearch) {
    t22 = debouncedSearch.length > 0 ? {
      search: debouncedSearch
    } : {};
    $[44] = debouncedSearch;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== creatorFilter) {
    t23 = creatorFilter !== undefined ? {
      creator: creatorFilter
    } : {};
    $[46] = creatorFilter;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== watchlistActive || $[49] !== watchlistPubkeys) {
    t24 = watchlistActive ? {
      pubkeys: watchlistPubkeys.length === 0 ? WATCHLIST_EMPTY_SENTINEL : watchlistPubkeys.join(",")
    } : {};
    $[48] = watchlistActive;
    $[49] = watchlistPubkeys;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== overhaulArgs || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22 || $[56] !== t23 || $[57] !== t24) {
    t25 = {
      fields: LAUNCHPAD_FEED_FIELDS,
      viewportGated: true,
      sortBy: t19,
      sortOrder: "desc",
      status: t20,
      ...t21,
      ...t22,
      ...t23,
      ...overhaulArgs,
      ...t24
    };
    $[51] = overhaulArgs;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  const query = useGetProposalsInfinite(t25);
  let t26;
  if ($[59] !== applyCategoryToUrl || $[60] !== outcomesBounds || $[61] !== setWatchlistActive) {
    t26 = function handleClearAll() {
      setOverhaul(buildEmptyProposalsOverhaul(outcomesBounds));
      applyCategoryToUrl([]);
      setStatus(null);
      setSearch("");
      setIsMyProposals(false);
      setWatchlistActive(false);
    };
    $[59] = applyCategoryToUrl;
    $[60] = outcomesBounds;
    $[61] = setWatchlistActive;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  const handleClearAll = t26;
  let t27;
  if ($[63] !== applyCategoryToUrl) {
    t27 = function handleFilterChange(key_1, value_0) {
      if (key_1 === "category") {
        const cats = asStrArr(value_0);
        onEvent("proposal_filter_changed", {
          filterType: "category",
          value: cats.length > 0 ? cats.join(",") : "all"
        });
        applyCategoryToUrl(cats);
        return;
      }
      setOverhaulValue(key_1, value_0);
      const filterType = PROPOSALS_FILTER_TYPE_BY_KEY[key_1];
      if (filterType !== undefined) {
        onEvent("proposal_filter_changed", {
          filterType,
          value: summariseFilterValue(value_0)
        });
      }
    };
    $[63] = applyCategoryToUrl;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const handleFilterChange = t27;
  let t28;
  if ($[65] !== overhaulArgs) {
    t28 = Object.keys(overhaulArgs);
    $[65] = overhaulArgs;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  const overhaulActive = t28.length > 0;
  const hasActiveFilters = overhaulActive || status !== null || debouncedSearch.length > 0 || isMyProposals || watchlistActive;
  const listIsLoading = query.isPending || watchlistLoading;
  const t29 = !listIsLoading;
  let t30;
  if ($[67] !== t29) {
    t30 = {
      name: "proposals",
      isReady: t29
    };
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  useRoutePerf(t30);
  if (listIsLoading) {
    let t31;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <ProposalFeedSkeleton />;
      $[69] = t31;
    } else {
      t31 = $[69];
    }
    return t31;
  }
  let t31;
  if ($[70] !== regionEnabled) {
    t31 = regionEnabled ? {} : {
      location: []
    };
    $[70] = regionEnabled;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== categorySelected || $[73] !== overhaul || $[74] !== t31) {
    t32 = {
      ...overhaul,
      ...t31,
      category: categorySelected
    };
    $[72] = categorySelected;
    $[73] = overhaul;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  const activeFilterValues = t32;
  let t33;
  if ($[76] !== t) {
    t33 = buildProposalSortOptions(t);
    $[76] = t;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== currencySymbol || $[79] !== exchangeRate || $[80] !== marketTypeFilter || $[81] !== outcomesBounds || $[82] !== regionEnabled || $[83] !== t || $[84] !== tFilters) {
    t34 = [...marketTypeFilter, ...buildProposalFilterDimensions(t, tFilters, outcomesBounds, regionEnabled, {
      rate: exchangeRate,
      symbol: currencySymbol
    })];
    $[78] = currencySymbol;
    $[79] = exchangeRate;
    $[80] = marketTypeFilter;
    $[81] = outcomesBounds;
    $[82] = regionEnabled;
    $[83] = t;
    $[84] = tFilters;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = next => {
      setIsMyProposals(next);
      onEvent("proposal_filter_changed", {
        filterType: "myProposals",
        value: String(next)
      });
    };
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== activeFilterValues || $[88] !== filterOpen || $[89] !== handleClearAll || $[90] !== handleFilterChange || $[91] !== isAuthenticated || $[92] !== isMyProposals || $[93] !== setFilterOpen || $[94] !== sortBy || $[95] !== t33 || $[96] !== t34) {
    t36 = <ProposalFeedToolbar sortOptions={t33} activeSort={sortBy} onSortChange={setSortBy} onSortEvent={_temp5} filters={t34} activeFilters={activeFilterValues} onFilterChange={handleFilterChange} onClearAll={handleClearAll} isAuthenticated={isAuthenticated} isMyProposals={isMyProposals} onMyProposalsChange={t35} filterOpen={filterOpen} onFilterOpenChange={setFilterOpen} />;
    $[87] = activeFilterValues;
    $[88] = filterOpen;
    $[89] = handleClearAll;
    $[90] = handleFilterChange;
    $[91] = isAuthenticated;
    $[92] = isMyProposals;
    $[93] = setFilterOpen;
    $[94] = sortBy;
    $[95] = t33;
    $[96] = t34;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  if ($[98] !== handleClearAll || $[99] !== hasActiveFilters || $[100] !== query) {
    t37 = <ProposalFeedList query={query} hasActiveFilters={hasActiveFilters} onClearFilters={handleClearAll} />;
    $[98] = handleClearAll;
    $[99] = hasActiveFilters;
    $[100] = query;
    $[101] = t37;
  } else {
    t37 = $[101];
  }
  let t38;
  if ($[102] !== t36 || $[103] !== t37) {
    t38 = <div className={LISTING_INDEX_PAGE_STACK_CLASS}>{t36}{t37}</div>;
    $[102] = t36;
    $[103] = t37;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  return t38;
}
function _temp5(value_1) {
  onEvent("proposal_filter_changed", {
    filterType: "sort",
    value: value_1
  });
}
function _temp4() {
  return Date.now();
}
function _temp3() {}
function _temp2() {}
function _temp(c) {
  return VALID_CATEGORIES.has(c);
}
function useProposalWatchlist(active) {
  const $ = _c(8);
  const {
    isAuthenticated,
    isLoading: authLoading
  } = useAuth();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      kind: "proposals",
      fields: ["pubkeys"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== active) {
    t1 = {
      enabled: active
    };
    $[1] = active;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isFetching
  } = useAuthenticatedWatchlist(t0, t1);
  const t2 = active && (authLoading || isAuthenticated && isFetching);
  let t3;
  if ($[3] !== data?.pubkeys) {
    t3 = data?.pubkeys ?? [];
    $[3] = data?.pubkeys;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      watchlistLoading: t2,
      watchlistPubkeys: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function proposalOutcomeBounds(configs: readonly {
  minOutcomes: number;
  maxOutcomes: number;
}[]): OutcomesBounds {
  return {
    min: configs.length === 0 ? FALLBACK_MIN_OUTCOMES : Math.min(...configs.map(config => config.minOutcomes)),
    max: configs.length === 0 ? FALLBACK_MAX_OUTCOMES : Math.max(...configs.map(config => config.maxOutcomes))
  };
}